/**
 * Tiny fetch-based API client. The auth store injects the bearer token.
 * Any 401 surfaces as ApiError with status=401 so the caller can boot the user.
 */
import { useAuthStore } from '../auth/store';

export class ApiError extends Error {
  override readonly name = 'ApiError';
  constructor(
    readonly status: number,
    message: string,
    readonly issues?: unknown,
  ) {
    super(message);
  }
}

const baseUrl = (() => {
  const env = import.meta.env.VITE_API_URL;
  if (env) return env.replace(/\/$/, '');
  // In dev we let Vite proxy /api → API; in prod, same-origin would be unusual,
  // so we expect VITE_API_URL to be set for the production build.
  if (import.meta.env.DEV) return '/api';
  return '';
})();

export function getApiBaseUrl(): string {
  return baseUrl;
}

/**
 * Rewrites an HTTP stream URL to go through the server-side proxy so it can be
 * played from an HTTPS admin page without mixed-content blocks. HTTPS URLs pass through unchanged.
 */
export function streamProxyUrl(url: string): string {
  if (!url.startsWith('http://')) return url;
  return `${baseUrl}/admin/stream-proxy?url=${encodeURIComponent(url)}`;
}

export type QueryValue = string | number | boolean | string[] | undefined | null;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Loose by design — typed call sites pass interface types that lack an index signature.
   * Narrowed at runtime: arrays append, scalars set, undefined/null skipped. */
  query?: object;
  signal?: AbortSignal;
  /** Skip the auth header (e.g. /auth/google itself). */
  anonymous?: boolean;
  /** Override the response parser; default JSON-or-empty. */
  raw?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(
    `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`,
    window.location.origin,
  );
  if (query) {
    for (const [k, v] of Object.entries(query as Record<string, unknown>)) {
      if (v === undefined || v === null || v === '') continue;
      if (Array.isArray(v)) {
        for (const item of v) {
          if (item === undefined || item === null) continue;
          url.searchParams.append(k, String(item));
        }
      } else if (typeof v !== 'object') {
        // Skip unknown object shapes; query is intended for scalars / string[].
        url.searchParams.set(k, String(v));
      }
    }
  }
  // For the dev proxy we want the relative URL; otherwise the full one.
  return baseUrl.startsWith('http') ? url.toString() : `${url.pathname}${url.search}`;
}

export async function apiFetch<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    accept: 'application/json',
  };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (!opts.anonymous) {
    const token = useAuthStore.getState().token;
    if (token) headers.authorization = `Bearer ${token}`;
  }

  const res = await fetch(buildUrl(path, opts.query), {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
    credentials: 'omit',
  });

  if (res.status === 401 && !opts.anonymous) {
    // Single source of truth: clear auth so the route guard re-routes to /sign-in.
    useAuthStore.getState().clear();
  }

  if (!res.ok) {
    const text = await res.text();
    let message = res.statusText || `HTTP ${res.status}`;
    let issues: unknown;
    if (text) {
      try {
        const parsed = JSON.parse(text);
        if (parsed && typeof parsed === 'object') {
          if (typeof (parsed as { error?: unknown }).error === 'string') {
            message = (parsed as { error: string }).error;
          } else if (typeof (parsed as { message?: unknown }).message === 'string') {
            message = (parsed as { message: string }).message;
          }
          if ((parsed as { issues?: unknown }).issues !== undefined) {
            issues = (parsed as { issues: unknown }).issues;
          }
        } else {
          message = text;
        }
      } catch {
        message = text;
      }
    }
    throw new ApiError(res.status, message, issues);
  }

  if (opts.raw) return (await res.text()) as unknown as T;

  // 204 / empty body → null
  const ct = res.headers.get('content-type') ?? '';
  if (res.status === 204) return null as unknown as T;

  // If the caller didn't ask for raw, anything other than JSON is a misconfiguration —
  // most often VITE_API_URL pointing at the SPA itself, which returns index.html and
  // would otherwise be silently coerced into a string and crash the consuming hook.
  if (!ct.includes('application/json')) {
    const sample = (await res.text()).slice(0, 120);
    throw new ApiError(
      res.status,
      `expected JSON but got ${ct || 'no content-type'} — check VITE_API_URL points at the API. First bytes: ${sample}`,
    );
  }
  return (await res.json()) as T;
}

/**
 * Fetches a binary resource (image / video / etc.) with the same auth pipeline
 * as `apiFetch`, returning a `Blob` the caller can turn into an Object URL.
 * Used by `<AuthedImage>` since `<img src>` doesn't carry Bearer tokens.
 */
export async function apiBlob(path: string, opts: { signal?: AbortSignal } = {}): Promise<Blob> {
  const headers: Record<string, string> = {};
  const token = useAuthStore.getState().token;
  if (token) headers.authorization = `Bearer ${token}`;

  const res = await fetch(buildUrl(path), {
    method: 'GET',
    headers,
    signal: opts.signal,
    credentials: 'omit',
  });
  if (res.status === 401) useAuthStore.getState().clear();
  if (!res.ok) throw new ApiError(res.status, res.statusText || `HTTP ${res.status}`);
  return res.blob();
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], opts?: RequestOptions) =>
    apiFetch<T>(path, { ...opts, method: 'GET', query }),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    apiFetch<T>(path, { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    apiFetch<T>(path, { ...opts, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    apiFetch<T>(path, { ...opts, method: 'PUT', body }),
  delete: <T>(path: string, opts?: RequestOptions) =>
    apiFetch<T>(path, { ...opts, method: 'DELETE' }),
};

/**
 * Multipart upload — `apiFetch` always JSON-stringifies the body, which
 * breaks file uploads. This bypasses that path: builds a FormData with the
 * file under the `file` field plus any extra string fields, lets the
 * browser set Content-Type to `multipart/form-data; boundary=…`, and
 * returns the parsed JSON response.
 */
export async function apiUpload<T = unknown>(
  path: string,
  file: File,
  extraFields: Record<string, string> = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  const token = useAuthStore.getState().token;
  if (token) headers.authorization = `Bearer ${token}`;

  const form = new FormData();
  form.append('file', file);
  for (const [k, v] of Object.entries(extraFields)) form.append(k, v);

  const res = await fetch(buildUrl(path), {
    method: 'POST',
    headers, // intentionally no content-type — browser sets the boundary
    body: form,
    credentials: 'omit',
  });

  if (res.status === 401) useAuthStore.getState().clear();
  if (!res.ok) {
    const text = await res.text();
    let message = res.statusText || `HTTP ${res.status}`;
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed === 'object' && typeof parsed.error === 'string') {
        message = parsed.error;
      }
    } catch {
      message = text || message;
    }
    throw new ApiError(res.status, message);
  }
  return (await res.json()) as T;
}
